import React, { useEffect, useMemo } from "react";
import { useDesign } from "../context/DesignContext";

/**
 * MKTRANSFE — Global Dynamic Design Bridge
 *
 * Objektif:
 * - Fè Design Manager la gen efè vizib sou tout ansyen paj yo,
 *   menm lè kèk components toujou gen Tailwind hard-coded.
 * - Sipòte nouvo Swiss Glass / Fintech template yo.
 * - Prepare frontend lan pou plizyè preset/template san manyen lojik finansye.
 * - Pa manyen API finansye, provider, transfè, PIN, KYC, wallet, elatriye.
 *
 * Mete component sa a yon sèl fwa anba DesignProvider nan App.js/App.jsx.
 */

const DEFAULTS = {
  primary: "#06b6d4",
  accent: "#0891b2",
  background: "#090f16",
  backgroundStart: "#020617",
  backgroundEnd: "#0f172a",
  surface: "rgba(255,255,255,0.03)",
  surfaceHover: "rgba(255,255,255,0.06)",
  surfaceStrong: "#0f172a",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  textPrimary: "#ffffff",
  textSecondary: "#94a3b8",
  textMuted: "#64748b",
  border: "rgba(255,255,255,0.10)",
  borderHover: "rgba(6,182,212,0.50)",
  radius: "12px",
  cardRadius: "16px",
  headingFont: "Inter",
  bodyFont: "Inter",
};

function setVar(root, key, value) {
  if (value !== undefined && value !== null && value !== "") {
    root.style.setProperty(key, String(value));
  }
}

export default function GlobalDynamicDesign() {
  const { design } = useDesign();

  const theme = design?.theme || {};
  const colors = theme?.colors || {};
  const layout = design?.layout || {};

  const resolved = useMemo(
    () => ({
      primary: colors.primary || DEFAULTS.primary,
      accent: colors.accent || DEFAULTS.accent,
      background: colors.background || DEFAULTS.background,
      backgroundStart: colors.backgroundStart || DEFAULTS.backgroundStart,
      backgroundEnd: colors.backgroundEnd || DEFAULTS.backgroundEnd,
      surface: colors.surface || DEFAULTS.surface,
      surfaceHover: colors.surfaceHover || DEFAULTS.surfaceHover,
      surfaceStrong: colors.surfaceStrong || DEFAULTS.surfaceStrong,
      success: colors.success || DEFAULTS.success,
      warning: colors.warning || DEFAULTS.warning,
      danger: colors.danger || DEFAULTS.danger,
      textPrimary: colors.textPrimary || DEFAULTS.textPrimary,
      textSecondary: colors.textSecondary || DEFAULTS.textSecondary,
      textMuted: colors.textMuted || DEFAULTS.textMuted,
      border: colors.border || DEFAULTS.border,
      borderHover: colors.borderHover || DEFAULTS.borderHover,

      radius: theme.borderRadius || DEFAULTS.radius,
      cardRadius: theme.cardRadius || DEFAULTS.cardRadius,

      headingFont: theme.headingFont || DEFAULTS.headingFont,
      bodyFont:
        !theme.fontFamily || theme.fontFamily === "system"
          ? DEFAULTS.bodyFont
          : theme.fontFamily,

      glassEffect: theme.glassEffect !== false,
      animations: theme.animations !== false,
      compactMode: theme.compactMode === true,

      dashboardStyle: layout.dashboardStyle || "glass",
      balanceStyle: layout.balanceStyle || "hero",
      quickActionsStyle: layout.quickActionsStyle || "glass-cards",
      desktopNavigation: layout.desktopNavigation || "sidebar",
      mobileNavigation: layout.mobileNavigation || "bottom",
    }),
    [
      colors,
      theme.borderRadius,
      theme.cardRadius,
      theme.headingFont,
      theme.fontFamily,
      theme.glassEffect,
      theme.animations,
      theme.compactMode,
      layout.dashboardStyle,
      layout.balanceStyle,
      layout.quickActionsStyle,
      layout.desktopNavigation,
      layout.mobileNavigation,
    ]
  );

  useEffect(() => {
    if (typeof document === "undefined") return;

    const root = document.documentElement;

    setVar(root, "--dm-primary", resolved.primary);
    setVar(root, "--dm-accent", resolved.accent);
    setVar(root, "--dm-background", resolved.background);
    setVar(root, "--dm-background-start", resolved.backgroundStart);
    setVar(root, "--dm-background-end", resolved.backgroundEnd);
    setVar(root, "--dm-surface", resolved.surface);
    setVar(root, "--dm-surface-hover", resolved.surfaceHover);
    setVar(root, "--dm-surface-strong", resolved.surfaceStrong);
    setVar(root, "--dm-success", resolved.success);
    setVar(root, "--dm-warning", resolved.warning);
    setVar(root, "--dm-danger", resolved.danger);
    setVar(root, "--dm-text-primary", resolved.textPrimary);
    setVar(root, "--dm-text-secondary", resolved.textSecondary);
    setVar(root, "--dm-text-muted", resolved.textMuted);
    setVar(root, "--dm-border", resolved.border);
    setVar(root, "--dm-border-hover", resolved.borderHover);
    setVar(root, "--dm-radius", resolved.radius);
    setVar(root, "--dm-card-radius", resolved.cardRadius);
    setVar(root, "--dm-heading-font", resolved.headingFont);
    setVar(root, "--dm-body-font", resolved.bodyFont);

    root.dataset.themeMode = theme.mode || "dark";
    root.dataset.animations = resolved.animations ? "on" : "off";
    root.dataset.glass = resolved.glassEffect ? "on" : "off";
    root.dataset.compact = resolved.compactMode ? "on" : "off";
    root.dataset.dashboardStyle = resolved.dashboardStyle;
    root.dataset.balanceStyle = resolved.balanceStyle;
    root.dataset.quickActionsStyle = resolved.quickActionsStyle;
    root.dataset.desktopNavigation = resolved.desktopNavigation;
    root.dataset.mobileNavigation = resolved.mobileNavigation;

    document.body.style.background = resolved.background;
    document.body.style.color = resolved.textPrimary;
  }, [resolved, theme.mode]);

  const css = useMemo(
    () => `
/* ==========================================================
   MKTRANSFE GLOBAL DYNAMIC OVERRIDES
   Design Manager -> ansyen + nouvo frontend components
========================================================== */

html,
body {
  background-color: var(--dm-background) !important;
  color: var(--dm-text-primary) !important;
}

#root {
  min-height: 100vh;
}

.App {
  min-height: 100vh;
}
body {
  font-family: var(--dm-body-font), system-ui, sans-serif !important;
  background:
    radial-gradient(circle at top right, color-mix(in srgb, var(--dm-primary) 8%, transparent), transparent 34%),
    linear-gradient(
      135deg,
      var(--dm-background-start),
      var(--dm-background-end)
    ) !important;
  min-height: 100vh;
}

h1,
h2,
h3,
h4,
h5,
h6,
.font-mono-num,
.dm-amount,
[data-design-number="true"] {
  font-family: var(--dm-heading-font), system-ui, sans-serif !important;
}

/* ---------- Backgrounds prensipal ---------- */
.bg-\\[\\#020812\\],
.bg-\\[\\#07111d\\],
.bg-\\[\\#081522\\],
.bg-\\[\\#071421\\],
.bg-\\[\\#040b14\\],
.bg-\\[\\#0a1320\\],
.bg-\\[\\#0a1724\\],
.bg-\\[\\#091725\\],
.bg-\\[\\#050d1b\\] {
  background-color: var(--dm-background) !important;
}

.dm-background {
  background: var(--dm-background) !important;
}

.dm-surface {
  background: var(--dm-surface) !important;
}

.dm-surface-strong {
  background: var(--dm-surface-strong) !important;
}

/* ---------- Cyan / Blue -> Primary ---------- */
.text-cyan-100,
.text-cyan-200,
.text-cyan-300,
.text-cyan-400,
.text-blue-100,
.text-blue-200,
.text-blue-300,
.text-blue-400 {
  color: var(--dm-primary) !important;
}

.bg-cyan-400,
.bg-cyan-500,
.bg-blue-400,
.bg-blue-500,
.bg-blue-600 {
  background-color: var(--dm-primary) !important;
}

.border-cyan-300,
.border-cyan-400,
.border-cyan-500,
.border-blue-300,
.border-blue-400,
.border-blue-500 {
  border-color: color-mix(in srgb, var(--dm-primary) 55%, transparent) !important;
}

.bg-cyan-400\\/10,
.bg-cyan-400\\/15,
.bg-cyan-500\\/10,
.bg-cyan-500\\/15,
.bg-blue-400\\/10,
.bg-blue-400\\/15,
.bg-blue-500\\/10,
.bg-blue-500\\/15 {
  background-color: color-mix(in srgb, var(--dm-primary) 15%, transparent) !important;
}

.border-cyan-400\\/20,
.border-cyan-400\\/25,
.border-cyan-400\\/30,
.border-cyan-400\\/35,
.border-cyan-400\\/40,
.border-blue-400\\/20,
.border-blue-400\\/25,
.border-blue-400\\/30,
.border-blue-400\\/35,
.border-blue-400\\/40 {
  border-color: color-mix(in srgb, var(--dm-primary) 35%, transparent) !important;
}

/* ---------- Accent ---------- */
.text-fuchsia-300,
.text-fuchsia-400,
.text-purple-300,
.text-purple-400 {
  color: var(--dm-accent) !important;
}

.bg-fuchsia-500,
.bg-purple-500 {
  background-color: var(--dm-accent) !important;
}

.bg-fuchsia-500\\/10,
.bg-fuchsia-500\\/15,
.bg-purple-500\\/10,
.bg-purple-500\\/15 {
  background-color: color-mix(in srgb, var(--dm-accent) 15%, transparent) !important;
}

/* ---------- Success ---------- */
.text-emerald-200,
.text-emerald-300,
.text-emerald-400,
.text-green-300,
.text-green-400 {
  color: var(--dm-success) !important;
}

.bg-emerald-400,
.bg-emerald-500,
.bg-green-500 {
  background-color: var(--dm-success) !important;
}

.bg-emerald-500\\/10,
.bg-emerald-500\\/15,
.bg-emerald-400\\/10,
.bg-emerald-400\\/15 {
  background-color: color-mix(in srgb, var(--dm-success) 15%, transparent) !important;
}

.border-emerald-400\\/20,
.border-emerald-400\\/25,
.border-emerald-400\\/30,
.border-emerald-500\\/30 {
  border-color: color-mix(in srgb, var(--dm-success) 35%, transparent) !important;
}

/* ---------- Warning ---------- */
.text-amber-200,
.text-amber-300,
.text-amber-400,
.text-yellow-300 {
  color: var(--dm-warning) !important;
}

.bg-amber-400,
.bg-amber-500,
.bg-yellow-500 {
  background-color: var(--dm-warning) !important;
}

.bg-amber-500\\/10,
.bg-amber-500\\/15,
.bg-amber-400\\/10,
.bg-yellow-500\\/15 {
  background-color: color-mix(in srgb, var(--dm-warning) 15%, transparent) !important;
}

/* ---------- Danger ---------- */
.text-red-200,
.text-red-300,
.text-red-400 {
  color: var(--dm-danger) !important;
}

.bg-red-400,
.bg-red-500 {
  background-color: var(--dm-danger) !important;
}

.bg-red-500\\/10,
.bg-red-500\\/15 {
  background-color: color-mix(in srgb, var(--dm-danger) 15%, transparent) !important;
}

/* ---------- Text ---------- */
.text-white {
  color: var(--dm-text-primary) !important;
}

.text-white\\/30,
.text-white\\/35,
.text-white\\/40,
.text-white\\/45,
.text-white\\/50,
.text-white\\/55,
.text-white\\/60,
.text-white\\/70 {
  color: var(--dm-text-secondary) !important;
}

.dm-muted {
  color: var(--dm-text-muted) !important;
}

/* ---------- Borders / surfaces ---------- */
.border-white\\/5,
.border-white\\/10,
.border-white\\/15,
.border-white\\/20,
.border-white\\/\\[0\\.05\\],
.border-white\\/\\[0\\.06\\],
.border-white\\/\\[0\\.07\\],
.border-white\\/\\[0\\.08\\] {
  border-color: var(--dm-border) !important;
}

.bg-white\\/5,
.bg-white\\/10,
.bg-white\\/\\[0\\.025\\],
.bg-white\\/\\[0\\.035\\],
.bg-white\\/\\[0\\.04\\],
.bg-white\\/\\[0\\.045\\],
.bg-white\\/\\[0\\.05\\] {
  background-color: var(--dm-surface) !important;
}

/* ---------- Gradients ---------- */
.bg-gradient-to-r.from-cyan-500,
.bg-gradient-to-br.from-cyan-400,
.bg-gradient-to-br.from-blue-400,
.bg-gradient-to-r.from-blue-500 {
  background-image: linear-gradient(
    135deg,
    var(--dm-primary),
    var(--dm-accent)
  ) !important;
}

/* ---------- Dynamic Card ---------- */
.dm-card {
  background: var(--dm-surface) !important;
  border: 1px solid var(--dm-border) !important;
  border-radius: var(--dm-card-radius) !important;
  transition: all 0.2s ease-in-out;
}

.dm-card:hover {
  background: var(--dm-surface-hover) !important;
  border-color: var(--dm-border-hover) !important;
  transform: translateY(-4px);
}

/* ---------- Glassmorphism ---------- */
html[data-glass="on"] .dm-card,
html[data-glass="on"] .mk-glass,
html[data-glass="on"] .dm-glass {
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(0,0,0,0.30);
}

/* ---------- Buttons ---------- */
.dm-primary {
  color: var(--dm-primary) !important;
}

.dm-primary-bg {
  background: var(--dm-primary) !important;
}

.dm-accent {
  color: var(--dm-accent) !important;
}

.dm-button-primary {
  min-height: 44px;
  background: var(--dm-primary) !important;
  color: #000 !important;
  border-radius: var(--dm-radius) !important;
  transition: all 0.2s ease-in-out;
}

.dm-button-primary:hover {
  filter: brightness(0.90);
  transform: translateY(-1px);
}

.dm-button-success {
  min-height: 44px;
  background: var(--dm-success) !important;
  color: #000 !important;
  border-radius: var(--dm-radius) !important;
}

.dm-button-secondary {
  min-height: 44px;
  background: color-mix(in srgb, var(--dm-text-primary) 10%, transparent) !important;
  color: var(--dm-text-primary) !important;
  border: 1px solid var(--dm-border) !important;
  border-radius: var(--dm-radius) !important;
}

/* ---------- Inputs ---------- */
.dm-input {
  min-height: 44px;
  background: color-mix(in srgb, #000 40%, transparent) !important;
  color: var(--dm-text-primary) !important;
  border: 1px solid var(--dm-border) !important;
  border-radius: var(--dm-radius) !important;
  outline: none;
}

.dm-input:focus {
  border-color: var(--dm-primary) !important;
  box-shadow: 0 0 0 1px var(--dm-primary) !important;
}

/* ---------- Status Badges ---------- */
.dm-badge-pending {
  background: color-mix(in srgb, var(--dm-warning) 20%, transparent) !important;
  color: var(--dm-warning) !important;
  border: 1px solid color-mix(in srgb, var(--dm-warning) 30%, transparent) !important;
}

.dm-badge-confirmed {
  background: color-mix(in srgb, var(--dm-success) 20%, transparent) !important;
  color: var(--dm-success) !important;
  border: 1px solid color-mix(in srgb, var(--dm-success) 30%, transparent) !important;
}

.dm-badge-rejected {
  background: color-mix(in srgb, var(--dm-danger) 20%, transparent) !important;
  color: var(--dm-danger) !important;
  border: 1px solid color-mix(in srgb, var(--dm-danger) 30%, transparent) !important;
}

/* ---------- Layout Presets ---------- */
html[data-dashboard-style="glass"] .dm-dashboard-card {
  background: var(--dm-surface) !important;
  border: 1px solid var(--dm-border) !important;
  border-radius: var(--dm-card-radius) !important;
}

html[data-balance-style="hero"] .dm-balance-card {
  text-align: center;
  background:
    linear-gradient(
      135deg,
      color-mix(in srgb, var(--dm-primary) 12%, transparent),
      var(--dm-surface)
    ) !important;
}

html[data-quick-actions-style="glass-cards"] .dm-quick-action {
  background: var(--dm-surface) !important;
  border: 1px solid var(--dm-border) !important;
  border-radius: var(--dm-card-radius) !important;
}

/* ---------- Compact mode ---------- */
html[data-compact="on"] .dm-card,
html[data-compact="on"] .dm-dashboard-card {
  padding: 12px !important;
}

html[data-compact="on"] .dm-section {
  gap: 10px !important;
}

/* ---------- Theme mode ---------- */
html[data-theme-mode="light"] body {
  color-scheme: light;
}

html[data-theme-mode="dark"] body {
  color-scheme: dark;
}

/* ---------- Disable animations ---------- */
html[data-animations="off"] *,
html[data-animations="off"] *::before,
html[data-animations="off"] *::after {
  animation: none !important;
  transition: none !important;
}

/* ---------- Mobile ---------- */
@media (max-width: 640px) {
  .dm-button-primary,
  .dm-button-success,
  .dm-button-secondary,
  .dm-input {
    min-height: 44px;
  }

  .dm-card,
  .dm-dashboard-card {
    border-radius: var(--dm-card-radius) !important;
  }
}
`,
    []
  );

  return (
    <style data-testid="global-dynamic-design-bridge">
      {css}
    </style>
  );
}